import {ReactComponent as CALENDAR} from "../images/calendarIcon.svg";
import {ReactComponent as LEFT} from "../images/arrow-left-gray.svg";
import {ReactComponent as RIGHT} from "../images/arrow-right-gray.svg";
import { useValidation, validateDate } from '@mui/x-date-pickers/validation';
import {
    useSplitFieldProps,
    useParsedFormat,
    usePickersContext,
} from '@mui/x-date-pickers/hooks';
import { IconButton, Stack } from '@mui/material';
import classes from "./YuJaDatePicker.module.css";
import {Button} from "@base-ui/react/button";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import React from "react";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import dayjs from 'dayjs';
import {getFormatedLocalDate} from "../utils/helpers";
import {NEXT_MONTH, PREVIOUS_MONTH} from "../utils/constants";

const ReadOnlyDateField = React.forwardRef((props, ref) => {
    const { internalProps, forwardedProps } = useSplitFieldProps(props, 'date');
    const { value, timezone, format, disabled } = internalProps;
    const { InputProps, slotProps, slots, sx, ...other } = forwardedProps;
    const handleRef = ref || InputProps?.ref;

    const pickersContext = usePickersContext();

    const parsedFormat = useParsedFormat(internalProps);
    const { hasValidationError } = useValidation({
        validator: validateDate,
        value,
        timezone,
        props: internalProps,
    });

    const handleTogglePicker = (event) => {
        if (pickersContext.open) {
            pickersContext.onClose(event);
        } else {
            pickersContext.onOpen(event);
        }
    };

    return (
        <Button
            aria-haspopup="dialog"
            aria-expanded={pickersContext.open ? "true" : "false"}
            onClick={handleTogglePicker}
            className={classes.dateInput}
            ref={handleRef}
            style={{...sx}}
        >
            {!value && <span className={classes.placeholder}>{parsedFormat}</span>}
            {!!value && <span>{value.format(format)}</span>}
            <CALENDAR color="action" style={{flexShrink: 0}}/>
        </Button>
    );
});


function MyCustomHeader(props) {
    const {view, currentMonth, onMonthChange, leftArrowButtonText, onViewChange, rightArrowButtonText } = props;

    const selectNextMonth = () => onMonthChange(currentMonth.add(1, 'month'), 'left');
    const selectPreviousMonth = () => onMonthChange(currentMonth.add(-1, 'month'), 'right');

    return (
        <Stack
            direction="row"
            alignItems={"center"} justifyContent={"space-between"}
            sx={{padding: '8px 16px', minHeight: 40}}
        >
            {/* Left Arrow */}
            <IconButton
                disableRipple={true}
                sx={{
                    '&:hover': { backgroundColor: '#F7F6FB' },
                }}
                onClick={selectPreviousMonth}
                title={leftArrowButtonText}
                size="small"
                aria-label={PREVIOUS_MONTH}
            >
                <LEFT />
            </IconButton>
            {/* Centered Month/Year */}
            <Button
                onClick={() => onViewChange(view === 'year' ? 'day' : 'year')}
                className={classes.toggleBtn}
                aria-label={view === 'year' ? "Switch to calendar view" : "Switch to year view"}
            >
                {currentMonth.format('MMMM YYYY')}
            </Button>

            {/* Right Arrow */}
            <IconButton
                disableRipple={true}
                sx={{
                    '&:hover': { backgroundColor: '#F7F6FB' },
                }}
                onClick={selectNextMonth}
                title={rightArrowButtonText}
                size="small"
                aria-label={NEXT_MONTH}
            >
                <RIGHT />
            </IconButton>
        </Stack>
    );
}


export default function YuJaDatePicker({
                                           date,
                                           onChange,
                                           minDate,
                                           maxDate,
                                           textFieldStyle={},
                                           disabled=false,
                                           ariaLabel
}) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                disabled={disabled}
                autoFocus={true}
                closeOnSelect={true}
                minDate={!!minDate ? dayjs(minDate) : null}
                maxDate={!!maxDate ? dayjs(maxDate) : null}
                slots={{
                    openPickerIcon: CALENDAR,
                    field: ReadOnlyDateField,
                    calendarHeader: MyCustomHeader,
                }}
                slotProps={{
                    field: {
                        sx: {
                            minWidth: 'unset !important',
                            ...textFieldStyle
                        }
                    },
                    popper: {
                        'aria-modal': 'true',
                        'aria-label': !!ariaLabel ? ariaLabel : 'Date picker',
                    },
                    desktopPaper: {
                        sx: {
                            margin: '5px 0',
                            '& .MuiDateCalendar-root': {
                                height: 'auto',
                            },

                            '& .MuiDayCalendar-slideTransition': {
                                height: 'auto',
                                minHeight: '230px'
                            },
                            '& .MuiDayCalendar-monthContainer': {
                                padding: '5px 0',
                            },
                            '& .MuiButtonBase-root:focus-visible, & .MuiPickersYear-yearButton:focus-visible': {
                                outlineOffset: '2px !important',

                            },
                            '& .Mui-focusVisible:not(.Mui-selected), & .MuiPickersYear-yearButton:focus-visible:not(.Mui-selected)': {
                                backgroundColor: '#F7F6FB !important',
                                background: '#F7F6FB !important'
                            },

                            '& .MuiPickersDay-root': {
                                margin: '2px'
                            },
                            '& .MuiPickersDay-today': {
                                backgroundColor: 'transparent'
                            },
                            '& .Mui-selected': {
                                backgroundColor: '#4a148c !important',
                                color: '#fff',
                            },
                            '& .MuiPickersDay-root:not(.Mui-selected):hover, & .MuiPickersYear-yearButton:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            },

                            '& .MuiPickersYear-yearButton': {
                                width: '42px',
                                height: '42px',
                                borderRadius: '50%',
                                fontSize: '12px'
                            }

                        },
                    },


                    mobilePaper: {
                        sx: {
                            '& .MuiDateCalendar-root': {
                                height: 'auto',
                            },

                            '& .MuiDayCalendar-slideTransition': {
                                height: 'auto',
                                minHeight: '230px'
                            },
                            '& .MuiDayCalendar-monthContainer': {
                                padding: '5px 0',
                            },
                            '& .MuiButtonBase-root:focus-visible, & .MuiPickersYear-yearButton:focus-visible': {
                                outlineOffset: '2px !important',

                            },
                            '& .Mui-focusVisible:not(.Mui-selected), & .MuiPickersYear-yearButton:focus-visible:not(.Mui-selected)': {
                                backgroundColor: '#F7F6FB !important',
                                background: '#F7F6FB !important'
                            },

                            '& .MuiPickersDay-root': {
                                margin: '2px'
                            },
                            '& .MuiPickersDay-today': {
                                backgroundColor: 'transparent'
                            },
                            '& .Mui-selected': {
                                backgroundColor: '#4a148c !important',
                                color: '#fff',
                            },
                            '& .MuiPickersDay-root:not(.Mui-selected):hover, & .MuiPickersYear-yearButton:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            },

                            '& .MuiPickersYear-yearButton': {
                                width: '42px',
                                height: '42px',
                                borderRadius: '50%',
                                fontSize: '12px'
                            }

                        },
                    },
                    actionBar: {
                        actions: [],
                    }

                }}

                format={'YYYY-MM-DD'}
                enableAccessibleFieldDOMStructure
                value={!!date ? dayjs(date) : null}
                onChange={val => onChange(!!val ? getFormatedLocalDate(val.toDate()) : null)}
                // onChange={(newValue) => {
                //     setValue(newValue);
                // }}
                // renderInput={(params) => <CustomInput {...params} />}
            />
        </LocalizationProvider>

    );
}
